import type { RouterOutputs } from "@repo/api";
import { formatIntToCurrency } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import type { ColumnDefWithTotal } from "~/types";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { api } from "~/lib/api";
import { useRegisterManager } from "~/managers/use-register-manager";
import { useReportStore } from "~/stores/use-report-store";

export const Route = createFileRoute("/_main/reports/_layout/day")({
  component: DayReports,
});

type Report = RouterOutputs["report"]["getDayReport"][0];

function DayReports() {
  const { range } = useReportStore();

  const { printDayReport } = useRegisterManager();

  const { t } = useTranslation("common");

  const { data: reportData } = api.report.getDayReport.useQuery({
    start: range.from,
    end: range.to,
  });

  const columns: ColumnDefWithTotal<Report>[] = [
    {
      id: t("Report.date"),
      accessorKey: "date",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        return rowA.original.date.getTime() - rowB.original.date.getTime();
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.date")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{format(report.date, "P")}</p>;
      },
    },
    {
      id: t("Report.number_of_receipts"),
      accessorKey: "numberOfReceipts",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.number_of_receipts")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.numberOfReceipts}</p>;
      },
    },
    {
      id: t("Report.customers"),
      accessorKey: "customers",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.customers")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.customers}</p>;
      },
    },
    {
      id: t("Report.revenue"),
      accessorKey: "revenue",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.revenue")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{formatIntToCurrency(report.revenue, false)}</p>;
      },
    },
    {
      id: t("Report.tips"),
      accessorKey: "tips",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.tips")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{formatIntToCurrency(report.tips, false)}</p>;
      },
    },
    {
      id: t("Report.vat"),
      accessorKey: "number",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.vat")} />
      ),
      cell: ({ row: { original: report } }) => {
        return (
          <p>
            {report.vat
              ? Object.entries(report.vat)
                  .map(
                    ([rate, value]) =>
                      `[${rate}%: ${formatIntToCurrency(value, false)}]`,
                  )
                  .join(", ")
              : "No VAT data available"}
          </p>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        defaultSorting={[{ id: t("Report.date"), desc: false }]}
        pdfExport={{
          filename: "day-report",
        }}
        onSelect={(row) => printDayReport(row)}
        csvExport={{
          filename: "day-report",
        }}
        columns={columns}
        data={reportData ?? []}
      />
    </div>
  );
}
